import { useState } from 'react';

import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import type { Incident } from '@/signalr';
import type { AppleEedCapabilities } from '@/signalr';
import {
  fetchAppleEedAdditionalData,
  requestAppleEedMedia as requestAppleEedMediaApi,
  type AppleEedCallInformation,
  type AppleEedCallerInformation,
} from '@ngsos/portal-core/commands';
import { appleEedAdditionalDataHasExpired, getAppleEedDataPanelActions } from '@ngsos/portal-core/incidents';
import { AdditionalDataSection } from './sections/additional/AdditionalDataSection';
import { CallerSection } from './sections/caller/CallerSection';
import { CallerLocationSection } from './sections/caller-location/CallerLocationSection';
import { CallerDataSourcesPanel } from './sections/diagnostics/CallerDataSourcesPanel';
import { IncidentCallsPanel } from './sections/diagnostics/IncidentCallsPanel';
import { AppleEedExpiredSection } from './sections/apple-eed/AppleEedExpiredSection';
import { AppleEedSection } from './sections/apple-eed/AppleEedSection';
import { GoogleElsSection } from './sections/google-els/GoogleElsSection';
import { IncidentSection } from './sections/incident/IncidentSection';
import { MobileAppSection } from './sections/mobile-app/MobileAppSection';
import { PatientSection } from './sections/patient/PatientSection';
import { UploadedMediaSection } from './sections/uploaded-media/UploadedMediaSection';

export type IncidentDataPanelProps = {
  incident: Incident;
  incidentIsReadOnly: boolean;
  appleEedCapabilities: AppleEedCapabilities | null;
};

export function IncidentDataPanel({ incident, incidentIsReadOnly, appleEedCapabilities }: IncidentDataPanelProps) {
  const agencyConfiguration = useAgencyConfiguration();
  const [appleEedCallInformation, setAppleEedCallInformation] = useState<AppleEedCallInformation | null>(null);
  const [appleEedMedicalData, setAppleEedMedicalData] = useState<AppleEedCallerInformation | null>(null);
  const [appleEedBatteryLevel, setAppleEedBatteryLevel] = useState<string | null>(null);
  const [askedForAppleEedMedicalData, setAskedForAppleEedMedicalData] = useState(false);
  const [appleEedMedicalDataIsLoading, setAppleEedMedicalDataIsLoading] = useState(false);
  const [appleEedMediaRequested, setAppleEedMediaRequested] = useState(false);

  const appleEedExpiresAtUtc = incident.appleEedAdditionalDataExpiresAtUtc;
  const {
    showPanel: showAppleEedPanel,
    canRetrieveAdditionalData,
    canRequestMedia,
  } = getAppleEedDataPanelActions(agencyConfiguration, appleEedCapabilities, appleEedExpiresAtUtc, incidentIsReadOnly);
  const appleEedHasExpired = appleEedAdditionalDataHasExpired(appleEedExpiresAtUtc);

  const getAppleEedMedicalData = async () => {
    if (!canRetrieveAdditionalData) {
      return;
    }

    const validPhoneNumber = appleEedCapabilities?.additionalData === true ? incident.caller.phoneNumber : null;
    if (validPhoneNumber == null) {
      return;
    }

    setAppleEedMedicalDataIsLoading(true);
    try {
      const additionalData = await fetchAppleEedAdditionalData(validPhoneNumber);
      setAskedForAppleEedMedicalData(true);
      if (additionalData === null) {
        return;
      }
      setAppleEedCallInformation(additionalData.additionalData?.call_information ?? null);
      setAppleEedMedicalData(additionalData.additionalData?.caller_information ?? null);
      setAppleEedBatteryLevel(additionalData.batteryLevel ?? null);
    } finally {
      setAppleEedMedicalDataIsLoading(false);
    }
  };

  const requestAppleEedMedia = async () => {
    if (!canRequestMedia) {
      return;
    }

    const validPhoneNumber = appleEedCapabilities?.mediaUpload === true ? incident.caller.phoneNumber : null;
    if (validPhoneNumber == null) {
      return;
    }

    await requestAppleEedMediaApi(incident.incidentId, validPhoneNumber);
    setAppleEedMediaRequested(true);
  };

  return (
    <div className="d-flex flex-column gap-3">
      <IncidentSection
        incidentId={incident.incidentId}
        incidentLabel={incident.label}
        purpose={incident.purpose}
        createdAtUtc={incident.createdAtUtc}
        closedAtUtc={incident.closedAtUtc}
        status={incident.isClosed}
        incidentIsReadOnly={incidentIsReadOnly}
        incidentInvolvedAgencyStates={incident.involvedAgencies}
        summary={incident.summary}
      />

      <PatientSection
        incidentId={incident.incidentId}
        description={incident.patient.description}
        incidentIsReadOnly={incidentIsReadOnly}
      />

      <CallerSection
        incidentId={incident.incidentId}
        caller={incident.caller}
        incidentIsReadOnly={incidentIsReadOnly}
      />

      <CallerLocationSection
        incidentId={incident.incidentId}
        incidentIsReadOnly={incidentIsReadOnly}
        callerPosition={incident.caller.currentLocation}
        callerPositionHistory={incident.caller.currentTrack}
        caller={incident.caller}
        chatIsActive={incident.caller.chatId != null}
      />

      <MobileAppSection
        source={incident.caller.dataSources.currentMobileApp}
        deviceInfo={incident.caller.deviceInfo}
        createdAtUtc={incident.createdAtUtc}
      />

      <GoogleElsSection source={incident.caller.dataSources.currentGoogleEls} createdAtUtc={incident.createdAtUtc} />

      {appleEedExpiresAtUtc != null && appleEedHasExpired && <AppleEedExpiredSection expiredAtUtc={appleEedExpiresAtUtc} />}

      {showAppleEedPanel && (
        <AppleEedSection
          appleEedCallInformation={appleEedCallInformation}
          appleEedMedicalData={appleEedMedicalData}
          appleEedBatteryLevel={appleEedBatteryLevel}
          canRetrieveAdditionalData={canRetrieveAdditionalData}
          canRequestMedia={canRequestMedia}
          askedForAppleEedMedicalData={askedForAppleEedMedicalData}
          appleEedMedicalDataIsLoading={appleEedMedicalDataIsLoading}
          askedForAppleEedMedia={appleEedMediaRequested}
          getAppleEedMedicalData={getAppleEedMedicalData}
          requestAppleEedMedia={requestAppleEedMedia}
        />
      )}

      {incident.caller.attachments.length > 0 && <UploadedMediaSection media={incident.caller.attachments} />}

      <AdditionalDataSection
        incidentId={incident.incidentId}
        callSign={incident.callSign}
        ateMessage={incident.ateMessage}
        incidentIsReadOnly={incidentIsReadOnly}
      />

      <CallerDataSourcesPanel dataSources={incident.caller.dataSources} snapshotRevision={incident.snapshotRevision} />

      <IncidentCallsPanel involvedAgencies={incident.involvedAgencies} agencyViews={incident.agencyViews} />
    </div>
  );
}
